import React, { MutableRefObject } from 'react'
import styled from '@emotion/styled'
import Icon, { AnimatedIcon } from 'shared/library/atoms/Icon'
import useModal from 'shared/state/useModal'
import HeaderModals, {
  ModalProps,
  MODAL_CONFIG,
  MODAL_IDS
} from './HeaderModals'
import Button from 'library/atoms/Button'
import useTranslation from 'next-translate/useTranslation'
import ReadNotificationDot from 'library/atoms/UnreadNotificationDot'
import { AnimatePresence } from 'framer-motion'
import useLayout from 'state/useLayout'
import Divider from 'shared/library/atoms/Divider'
import { SIDEBAR_ID } from 'enums/sidebar'
import { LayoutContextProps } from 'providers/LayoutProvider'
import { useSidebar } from 'state/useSidebar'
import Link from 'next/link'
import routes from 'enums/routes'
import { useRouter } from 'next/router'
import useNotifications from 'state/useNotifications'

interface HeaderProps {
  /**
   * The reference is used in combination with the Sidebar component to trigger
   * click away functions.
   */
  menuRef?: MutableRefObject<HTMLButtonElement | null>
}

const Header = ({ menuRef }: HeaderProps) => {
  const { t } = useTranslation()
  const { notifications } = useNotifications()
  const { layout, sidebar, header } = useLayout()
  const { sidebarIsVisible, toggleSidebarIsVisible } = useSidebar()
  const { query, back } = useRouter()

  const outletSlug = query.outletSlug as string | undefined
  const { activeModal, setActiveModalId, currentModalProps } =
    useModal<ModalProps>({
      MODAL_CONFIG,
      modalPropVariations: {
        notifications: {}
      }
    })

  return (
    <>
      <StyledHeader>
        {/**
         * This section is reserved to be aligned with the Sidebar component.
         * It has a fixed width that reflects the Sidebar's style and position.
         */}
        <StyledSidebarContent>
          {sidebar?.isIncluded && (
            <StyledMenuButton
              variant="icon"
              aria-label={t('common:hamburger-menu')}
              onClick={() => toggleSidebarIsVisible()}
              aria-expanded={!!sidebarIsVisible}
              aria-controls={SIDEBAR_ID}
              ref={menuRef}
            >
              <StyledAnimatedIcon
                name="menu"
                freezeOnLastFrame
                reverseOnEvenClick
                size={16}
              />
            </StyledMenuButton>
          )}
          <StyledLogo name="logo" size={70} color="grey" />
        </StyledSidebarContent>
        <StyledDivider variant="solid" position="vertical" color="secondary" />
        <StyledHeaderContent layout={layout}>
          {header?.backButtonVisible && (
            <StyledBackButton variant="secondary" mb={4} onClick={() => back()}>
              <Icon name="arrow-left" mr={1} />
              {t('common:back')}
            </StyledBackButton>
          )}
          <StyledHeaderContentButtons>
            {outletSlug && (
              <Link href={routes.SEARCH.url({ outletSlug })} passHref>
                <Button variant="secondary">
                  <Icon name="search" aria-hidden color="currentColor" mr={3} />
                  {t('common:search')}
                </Button>
              </Link>
            )}
            <StyledButton
              variant="icon"
              aria-label={t('common:notifications')}
              onClick={() => setActiveModalId(MODAL_IDS.notifications)}
            >
              <Icon
                name="bell"
                title={t('common:notifications')}
                color="grey"
              />
              <AnimatePresence>
                {notifications?.length && !notifications?.[0]?.read && (
                  <ReadNotificationDot />
                )}
              </AnimatePresence>
            </StyledButton>
          </StyledHeaderContentButtons>
        </StyledHeaderContent>
      </StyledHeader>

      <HeaderModals
        activeModal={activeModal}
        setActiveModalId={setActiveModalId}
        {...currentModalProps}
      />
    </>
  )
}

export default Header

const HEADER_PADDING = 5

export const StyledHeader = styled.header(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  position: 'sticky',
  zIndex: theme.zIndex.header,
  top: 0,
  height: theme.layout.headerHeight,
  flexShrink: 0, // Prevent the content layout from shrinking the header
  paddingLeft: theme.space[HEADER_PADDING],
  background: theme.colors.background.primary,
  [theme.mediaQueries.lg]: {
    marginBottom: theme.space[0]
  }
}))

const StyledSidebarContent = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.space[3],
  [theme.mediaQueries.lg]: {
    width: `calc(${theme.layout.sidebarWidth}px - ${theme.space[HEADER_PADDING]}px)`
  }
}))

const StyledBackButton = styled(Button)(({ theme }) => ({
  marginBottom: 0,
  display: 'none',
  [theme.mediaQueries.md]: {
    display: 'block'
  }
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
  display: 'none',
  height: 24,
  alignSelf: 'center',
  margin: theme.mixins.pxSpread([0, theme.space[3]]),
  [theme.mediaQueries.md]: {
    display: 'block'
  },
  [theme.mediaQueries.lg]: {
    display: 'none'
  }
}))

const StyledHeaderContent = styled.div<{
  layout: LayoutContextProps['layout']
}>(({ theme, layout }) => ({
  display: 'flex',
  gap: theme.space[3],
  alignItems: 'center',
  justifyContent: 'space-between',
  flex: 1,
  ...theme.mixins.centeredContainer(theme.layout[layout].maxWidthSize, 5),
  [theme.mediaQueries.md]: {
    // When on tablet, there needs to be even spacing between the eola logo
    // and the page title. This 0 left padding creates this visual effect.
    paddingLeft: 0
  },
  [theme.mediaQueries.lg]: {
    ...theme.mixins.centeredContainer(theme.layout[layout].maxWidthSize, 5)
  }
}))

const StyledHeaderContentButtons = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto',
  gap: theme.space[4]
}))

const StyledLogo = styled(Icon)(({ theme }) => ({
  display: 'none',
  [theme.mediaQueries.lg]: {
    display: 'block'
  }
}))

const StyledButton = styled(Button)(() => ({
  width: 42,
  height: 42
}))

const StyledMenuButton = styled(StyledButton)(({ theme }) => ({
  [theme.mediaQueries.lg]: {
    display: 'none'
  }
}))

const StyledAnimatedIcon = styled(AnimatedIcon)(() => ({
  // Using this approach because we can't easily change the colour of the AnimatedIcon
  opacity: 0.6
}))
